import { FC, useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import { Box, Typography } from '@mui/material';

import CustomInput from '../../../../../common/CustomInput';
import CustomButton from '../../../../../common/CustomButton';
import CustomSelect from '../../../../../common/CustomSelect';
import { useGetLocationsQuery } from '../../../../../store/services/authApi';
import { useUpdateUserInfoMutation } from '../../../../../store/services/customizeOptionsApi';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import { setUserInfo } from '../../../../../store/slices/userSlice';
import CustomAutoSelect from '../../../../../common/CustomAutoSelect';

import styles from './AccountInfo.module.scss';

type FormikValues = {
  firstName?: string;
  lastName?: string;
  username?: string;
  gender?: string;
  agegroup?: string;
  country?: string;
};

const AccountInfo: FC = () => {
  const [countryInput, setCountryInput] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const { data: countries } = useGetLocationsQuery({ search: countrySearch, type: 'country' });
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.user);
  const [updateUserInfo, { isSuccess }] = useUpdateUserInfoMutation();

  useEffect(() => {
    const timeout = setTimeout(() => {
      setCountrySearch(countryInput);
    }, 500);

    return () => clearTimeout(timeout);
  }, [countryInput]);

  const initialValues = {
    firstName: user.first_name ?? '',
    lastName: user.last_name ?? '',
    username: user.username ?? '',
    gender: user.gender ?? '',
    agegroup: user.age_group ?? '',
    country: user.country ?? '',
  };

  const handleSubmit = async (values: FormikValues, { setFieldError }: any) => {
    const firstName = values.firstName?.trim() ?? '';
    const lastName = values.lastName?.trim() ?? '';
    const username = values.username?.trim() ?? '';

    const payload = {
      id: user.id,
      first_name: firstName,
      last_name: lastName,
      email: user.email,
      username,
      gender: values.gender,
      age_group: values.agegroup,
      country: values.country,
      nearest_city: user.nearest_city,
      nearest_airport: user.nearest_airport,
      chosen_plan: user.chosen_plan,
      primary_travel_purposes: user.primary_travel_purposes,
      destination_types: user.destination_types,
      activity_interests: user.activity_interests,
      travel_preference: user.travel_preference,
      budget_preference: user.budget_preference,
      pace_preference: user.pace_preference,
    };

    try {
      await updateUserInfo(payload).unwrap();
      dispatch(setUserInfo(payload));
    } catch (error: any) {
      console.error('Error updating user info:', error);
      const errors = error?.data?.errors;
      errors?.forEach((e: any) => {
        if (e.attr === 'first_name') setFieldError('firstName', e.detail);
        if (e.attr === 'last_name') setFieldError('lastName', e.detail);
        if (e.attr === 'username') setFieldError('username', e.detail);
      });
    }
  };

  const formikValidate = (values: FormikValues) => {
    const errors: FormikValues = {};
    if (!values.firstName?.trim()) {
      errors.firstName = 'First name is required';
    }
    if (!values.lastName?.trim()) {
      errors.lastName = 'Last name is required';
    }
    if (!values.username?.trim()) {
      errors.username = 'Username is required';
    }
    if (!values.gender) {
      errors.gender = 'Gender is required';
    }
    if (!values.agegroup) {
      errors.agegroup = 'Age group is required';
    }
    if (!values.country) {
      errors.country = 'Country is required';
    }
    return errors;
  };

  return (
    <Box className={styles.accountInfo}>
      <Typography
        variant="h7Bold"
        sx={{ color: '#222B3A' }}
      >
        Account Info
      </Typography>
      <Formik
        initialValues={initialValues}
        validate={formikValidate}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ values, errors, handleChange, isSubmitting, setFieldValue }) => (
          <Form className={styles.accountInfoForm}>
            <Box className={styles.nameInputs}>
              <CustomInput
                inputLabel="First Name"
                inputType="text"
                inputName="firstName"
                value={values.firstName}
                error={errors.firstName}
                customClass={styles.customInput}
                onChange={handleChange}
              />
              <CustomInput
                inputLabel="Last Name"
                inputType="text"
                inputName="lastName"
                value={values.lastName}
                error={errors.lastName}
                customClass={styles.customInput}
                onChange={handleChange}
              />
            </Box>
            <CustomInput
              inputType="text"
              inputName="username"
              required
              inputLabel="Username"
              value={values.username}
              error={errors.username}
              customClass={styles.customInput}
              onChange={handleChange}
            />
            <CustomSelect
              name="gender"
              inputLabel="Gender"
              handleChange={handleChange}
              required={true}
              value={values.gender}
              data={['Male', 'Female', 'Non-binary', 'Prefer not to say']}
              labelByValue={{ 'Non-binary': 'Non-binary/Other' }}
            />
            <CustomSelect
              name="agegroup"
              inputLabel="Age group"
              handleChange={handleChange}
              required={true}
              value={values.agegroup}
              data={['18-24', '25-34', '35-44', '45-54', '55-64', '65+']}
            />
            <CustomAutoSelect
              options={countries ?? []}
              inputLabel="Country"
              placeholder="Enter country name"
              handleChange={(e, value) => setFieldValue('country', value ? value.name : '')}
              onInputChange={(event, inputValue) => {
                setCountryInput(inputValue);
              }}
              value={values.country ? { name: values.country } : null}
            />

            <CustomButton
              btnType="submit"
              customClass={styles.customButton}
              disabled={
                isSubmitting ||
                !values.firstName?.trim() ||
                !values.lastName?.trim() ||
                !values.username?.trim() ||
                !values.gender ||
                !values.agegroup ||
                !values.country
              }
            >
              Save Changes
            </CustomButton>

            {isSuccess && (
              <Typography
                variant="h12"
                sx={{ color: '#208B64' }}
              >
                Account info updated successfully.
              </Typography>
            )}
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default AccountInfo;
